import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "secondary-font primary-btn",
        secondary: "secondary-font bg-secondary text-primary shadow-sm hover:bg-secondary/80",
        destructive: "secondary-font bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
        outline: "secondary-font outline-primary-btn",
        "outline-destructive":
          "border border-destructive bg-background text-destructive shadow-sm hover:text-destructive-foreground hover:bg-destructive",
        ghost: "",
        link: "text-primary-color underline-offset-4 hover:underline hover:text-primary-color/80 transition-colors duration-200",
      },
      size: {
        default: "h-9 rounded-full px-4 py-2",
        xs: "h-6 rounded-full px-2 text-xs",
        sm: "h-8 rounded-full px-3 text-xs",
        lg: "h-10 rounded-full px-8",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
  VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button }
